// theme.ts - Updated version with comprehensive form styling
import { alpha, createTheme } from '@mui/material';

const INFO_MAIN = '#7BBCB3';

interface LandingPagePalette {
  activeLight: string;
  activeDark: string;
  pencilsDownLight: string;
  pencilsDownDark: string;
  archivedLight: string;
  archivedDark: string;
  contentBorder: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    landingPage: LandingPagePalette;
  }
  interface PaletteOptions {
    landingPage?: LandingPagePalette;
  }
  interface TypeBackground {
    card?: string;
    accent?: string;
  }
}

export const theme = createTheme({
  spacing: 4,
  shape: { borderRadius: 8 },
  components: {
    MuiPaper: {
      defaultProps: {
        className: 'paper',
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0E463F',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: '"Montserrat", sans-serif',
        },
        sizeLarge: {
          borderRadius: 6,
        },
        sizeSmall: {
          borderRadius: 4,
        },
        sizeMedium: {
          borderRadius: 4,
        },
        // Add specific button variants styling
        contained: ({ theme }) => ({
          '&.MuiButton-containedPrimary': {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.success.main,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabled,
              color: theme.palette.text.disabled,
            },
          },
          '&.MuiButton-containedSecondary': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: theme.palette.background.default,
            },
            '&.Mui-disabled': {
              backgroundColor: 'gray',
              color: theme.palette.text.disabled,
            },
          },
        }),
        outlined: {
          '&.MuiButton-outlinedPrimary': {
            borderColor: '#7BBCB3',
            color: '#7BBCB3',
            '&:hover': {
              borderColor: '#E8FFFC',
              color: '#E8FFFC',
              backgroundColor: 'rgba(232, 255, 252, 0.04)',
            },
          },
        },
        text: ({ theme }) => ({
          '&.MuiButton-textPrimary': {
            color: '#A0CCD6',
            '&:hover': {
              backgroundColor: alpha(INFO_MAIN, 0.04),
            },
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabled,
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.8125rem',
        },
      },
    },
    // Checkbox component styling
    MuiCheckbox: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-checked': {
            color: '#7BBCB3',
          },
          '&:hover': {
            backgroundColor: alpha(INFO_MAIN, 0.08),
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
          },
        },
      },
    },
    // TextField component styling
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            height: '40px',
            '& input': {
              padding: '8.5px 14px',
            },
            '& textarea': {
              color: '#FFFFFF',
            },
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#7BBCB3',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-outlined': {
            color: '#A0CCD6',
            '&.Mui-focused': {
              color: '#7BBCB3',
            },
          },
          '& .MuiFormHelperText-root': {
            color: '#A0CCD6',
            '&.Mui-error': {
              color: '#FF9800',
            },
          },
        },
      },
    },
    // Dialog styling
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0E463F',
          color: '#FFFFFF',
          borderRadius: 4,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          fontSize: '1.25rem',
          fontFamily: '"Montserrat", sans-serif',
          fontWeight: 700,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 24px 16px',
          // Ensure proper spacing between dialog title and content
          '&:first-of-type': {
            paddingTop: '8px',
          },
          // Add small padding to prevent label clipping
          '& > .MuiStack-root:first-of-type': {
            paddingTop: '4px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 24px 16px',
          '& .MuiButton-text': {
            color: '#A0CCD6',
          },
          '& .MuiButton-contained': {
            backgroundColor: '#7BBCB3',
            color: '#0E463F',
            '&:hover': {
              backgroundColor: '#E8FFFC',
            },
          },
        },
      },
    },
    // Table components
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha(INFO_MAIN, 0.3)}`,
          color: '#FFFFFF',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#0E463F',
          fontFamily: '"Montserrat", sans-serif',
        },
      },
    },
    // Tab component styling
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: '#7BBCB3',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            color: '#FFFFFF',
          },
          '&:hover': {
            color: '#E8FFFC',
          },
        },
      },
    },
    // Stack component for form layouts
    MuiStack: {
      defaultProps: {
        spacing: 3, // 12px with spacing factor of 4
      },
    },
    // OutlinedInput styling for consistent heights
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-sizeSmall': {
            height: '40px',
            '& input': {
              padding: '8.5px 14px',
            },
          },
          '&.MuiInputBase-multiline': {
            height: 'auto',
            alignItems: 'baseline', // prevents content clipping
          },
        },
      },
    },
    // Select and MenuItem styling
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: '#FFFFFF',
          '&.MuiInputBase-sizeSmall': {
            height: '40px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7BBCB3',
            borderWidth: 2,
          },
          '& .MuiSelect-icon': {
            color: '#A0CCD6',
          },
        },
      },
    },
    // Menu styling for dropdowns
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0E463F',
          border: `1px solid ${alpha(INFO_MAIN, 0.3)}`,
          marginTop: 4,
          '& .MuiList-root': {
            padding: 0,
          },
        },
        list: {
          padding: 0,
          backgroundColor: '#0E463F',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: '#0E463F',
          color: '#FFFFFF',
          padding: '8px 16px',
          '&:hover': {
            backgroundColor: '#0a3530',
          },
          '&.Mui-selected': {
            backgroundColor: alpha(INFO_MAIN, 0.3),
            '&:hover': {
              backgroundColor: alpha(INFO_MAIN, 0.15),
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#A0CCD6',
          '&.Mui-focused': {
            color: '#7BBCB3',
          },
          // Ensure labels don't get clipped in dialogs
          '&.MuiInputLabel-outlined': {
            '&.MuiInputLabel-shrink': {
              backgroundColor: 'transparent',
              padding: '0 4px',
              marginLeft: '-4px',
            },
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
        // fullWidth: true, // this styling was causing major issues with the <GridToolbarFilterButton /> in ProjectTools.tsx, so I disabled
      },
      styleOverrides: {
        root: {
          '& .MuiInputLabel-outlined': {
            color: '#A0CCD6',
            '&.Mui-focused': {
              color: '#7BBCB3',
            },
          },
          '& .MuiOutlinedInput-root': {
            height: '40px',
          },
        },
      },
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#0E463F',
    },
    secondary: {
      main: '#FFFFFF',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#A0CCD6',
      tertiary: 'rgb(43,125,75)',
      disabled: 'rgba(255,255,255,0.8)',
    },
    background: {
      default: '#046156',
      paper: '#0E463F',
      card: '#1E6E64',
      accent: '#083237',
    },
    info: {
      main: '#7BBCB3',
      dark: '#000',
    },
    success: {
      main: '#E8FFFC',
    },
    warning: {
      main: '#FF9800',
    },
    landingPage: {
      activeLight: '#D5E4D5',
      activeDark: '#CCDCCC',
      pencilsDownLight: '#0E3C37',
      pencilsDownDark: '#0B3631',
      archivedLight: '#89AFAB',
      archivedDark: '#7FA5A1',
      contentBorder: '#003D36',
    },
    divider: alpha(INFO_MAIN, 0.6),
    action: {
      hover: alpha(INFO_MAIN, 0.15),
      selected: alpha(INFO_MAIN, 0.3),
      selectedOpacity: 0.3,
      hoverOpacity: 0.15,
      focus: alpha(INFO_MAIN, 0.2),
      focusOpacity: 0.2,
      disabled: alpha(INFO_MAIN, 0.8),
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  typography: {
    fontFamily: '"Roboto Slab", serif',
    fontWeightBold: 700,
    fontWeightRegular: 350,
    fontWeightMedium: 600,
    fontWeightLight: 300,
    fontSize: 12,
    subtitle1: {
      fontFamily: '"Montserrat", sans-serif',
    },
    subtitle2: {
      fontFamily: '"Montserrat", sans-serif',
    },
    h1: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: '700',
    },
    h2: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: '700',
    },
    h3: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: '700',
    },
    h4: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: '700',
    },
    h5: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: '700',
    },
    h6: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: '700',
    },
  },
});
